import { useEffect, useState } from 'react';
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/Form';
import { RefreshButton } from '../components/RefreshButton';
import { useToast } from '../components/Toast';

interface DayBucket {
  day: string; // YYYY-MM-DD
  orders: number;
  delivered: number;
  cancelled: number;
  revenueCents: number;
}

interface Snapshot {
  totalRevenueCents: number;
  deliveredCount: number;
  cancelledCount: number;
  totalOrders: number;
  completionPct: number;
  utilizationPct: number;
  driverCount: number;
  onlineDrivers: number;
  byDay: DayBucket[];
  loading: boolean;
}

const DAYS = 14;

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dollar(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function AnalyticsPage() {
  const toast = useToast();
  const [snap, setSnap] = useState<Snapshot>({
    totalRevenueCents: 0,
    deliveredCount: 0,
    cancelledCount: 0,
    totalOrders: 0,
    completionPct: 0,
    utilizationPct: 0,
    driverCount: 0,
    onlineDrivers: 0,
    byDay: [],
    loading: true,
  });
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRefreshing(true);

    (async () => {
      try {
        const since = new Date();
        since.setDate(since.getDate() - DAYS + 1);
        since.setHours(0, 0, 0, 0);

        // Pull recent orders for revenue + per-day rollups. Orders store
        // createdAt as ISO-8601 strings (written by the Flutter apps), so
        // compare against an ISO string — not a Firestore Timestamp.
        // Capped at 1000 documents to keep this page snappy; longer windows
        // should move to a Cloud Function aggregator.
        const ordersSnap = await getDocs(
          query(
            collection(db, 'orders'),
            where('createdAt', '>=', since.toISOString()),
            limit(1000)
          )
        );

        // Initialize day buckets so we always show DAYS columns even on
        // empty days.
        const buckets = new Map<string, DayBucket>();
        for (let i = 0; i < DAYS; i++) {
          const d = new Date(since);
          d.setDate(d.getDate() + i);
          const key = ymd(d);
          buckets.set(key, {
            day: key,
            orders: 0,
            delivered: 0,
            cancelled: 0,
            revenueCents: 0,
          });
        }

        let totalRevenueCents = 0;
        let deliveredCount = 0;
        let cancelledCount = 0;
        const totalOrders = ordersSnap.size;

        ordersSnap.forEach((doc) => {
          const o = doc.data() as Record<string, unknown>;
          const created = o.createdAt;
          let day: string | null = null;
          if (
            created &&
            typeof created === 'object' &&
            'toDate' in (created as Timestamp)
          ) {
            day = ymd((created as Timestamp).toDate());
          } else if (typeof created === 'string') {
            const d = new Date(created);
            if (!isNaN(d.getTime())) day = ymd(d);
          }
          const status = (o.status as string) ?? '';
          // Revenue: customer-facing total lives in pricingBreakdown.total
          // (dollars); fall back to finalCost / estimatedCost for legacy docs.
          const breakdown = o.pricingBreakdown as
            | Record<string, unknown>
            | undefined;
          const totalDollars =
            (typeof breakdown?.total === 'number' ? breakdown.total : null) ??
            (typeof o.finalCost === 'number' ? o.finalCost : null) ??
            (typeof o.estimatedCost === 'number' ? o.estimatedCost : 0);
          const totalCents = Math.round(totalDollars * 100);

          if (status === 'delivered') {
            deliveredCount += 1;
            totalRevenueCents += totalCents;
          } else if (status === 'cancelled') {
            cancelledCount += 1;
          }

          if (day && buckets.has(day)) {
            const b = buckets.get(day)!;
            b.orders += 1;
            if (status === 'delivered') {
              b.delivered += 1;
              b.revenueCents += totalCents;
            } else if (status === 'cancelled') {
              b.cancelled += 1;
            }
          }
        });

        // Driver utilization: online drivers / total drivers.
        const [driverCountAgg, onlineDriversAgg] = await Promise.all([
          getCountFromServer(
            query(
              collection(db, 'users'),
              where('roles', 'array-contains', 'driver')
            )
          ),
          getCountFromServer(
            query(collection(db, 'users'), where('isOnline', '==', true))
          ),
        ]);
        const driverCount = driverCountAgg.data().count;
        const onlineDrivers = onlineDriversAgg.data().count;

        const completionDenominator = deliveredCount + cancelledCount;
        const completionPct = completionDenominator
          ? (deliveredCount / completionDenominator) * 100
          : 0;
        const utilizationPct = driverCount
          ? (onlineDrivers / driverCount) * 100
          : 0;

        if (cancelled) return;
        setSnap({
          totalRevenueCents,
          deliveredCount,
          cancelledCount,
          totalOrders,
          completionPct,
          utilizationPct,
          driverCount,
          onlineDrivers,
          byDay: Array.from(buckets.values()),
          loading: false,
        });
        setLastUpdated(new Date());
      } catch (e) {
        console.error('Analytics load failed', e);
        if (!cancelled) {
          setSnap((s) => ({ ...s, loading: false }));
          toast.error('Failed to load analytics.');
        }
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reloadKey, toast]);

  const maxRevenue = Math.max(1, ...snap.byDay.map((d) => d.revenueCents));

  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle={`Last ${DAYS} days · client-side rollup of up to 1,000 orders.`}
      >
        <RefreshButton
          lastUpdated={lastUpdated}
          busy={refreshing}
          onClick={() => setReloadKey((k) => k + 1)}
        />
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Revenue (delivered)"
          value={snap.loading ? '…' : dollar(snap.totalRevenueCents)}
          hint={`${snap.deliveredCount} delivered orders`}
        />
        <Stat
          label="Completion %"
          value={
            snap.loading ? '…' : `${snap.completionPct.toFixed(1)}%`
          }
          hint={`${snap.deliveredCount} delivered / ${
            snap.deliveredCount + snap.cancelledCount
          } resolved`}
        />
        <Stat
          label="Driver utilization"
          value={
            snap.loading ? '…' : `${snap.utilizationPct.toFixed(1)}%`
          }
          hint={`${snap.onlineDrivers} online / ${snap.driverCount} total`}
        />
        <Stat
          label="Total orders"
          value={snap.loading ? '…' : snap.totalOrders.toLocaleString()}
          hint={`${snap.cancelledCount} cancelled`}
        />
      </div>

      <Card title={`Revenue by day · last ${DAYS} days`}>
        {snap.loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-2">
            {snap.byDay.map((d) => (
              <div key={d.day} className="flex items-center gap-3">
                <div className="w-24 shrink-0 font-mono text-xs text-slate-500">
                  {d.day}
                </div>
                <div className="relative h-6 flex-1 overflow-hidden rounded bg-slate-100">
                  <div
                    className="h-full bg-brand-500"
                    style={{
                      width: `${(d.revenueCents / maxRevenue) * 100}%`,
                    }}
                  />
                </div>
                <div className="w-28 shrink-0 text-right text-xs text-slate-700">
                  {dollar(d.revenueCents)}
                </div>
                <div className="w-32 shrink-0 text-right text-xs text-slate-500">
                  {d.delivered}d / {d.cancelled}c / {d.orders}t
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <Card>
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </Card>
  );
}
